
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { FaGoogle } from "react-icons/fa6";
import { SiAmazon } from "react-icons/si";
import { VscAzure } from "react-icons/vsc";
import { AlertCircle, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";

interface TenantCloudConfigureDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cloudProvider: string;
    config: { name: string };
    cloudConfigData: any;
    setCloudConfigData: (data: any) => void;
    onConfigured: () => void;
    subUserReg: () => Promise<boolean>;
}

const cloudConfig: Record<string, { icon: any; name: string; fullName: string; label: string }> = {
    gcp: { icon: FaGoogle, name: "GCP", fullName: "Google Cloud Platform", label: "GCP" },
    aws: { icon: SiAmazon, name: "AWS", fullName: "Amazon Web Services", label: "AWS" },
    azure: { icon: VscAzure, name: "Azure", fullName: "Microsoft Azure", label: "Azure" },
};

const configInputClass =
    "h-11 rounded-xl border border-slate-200 bg-white text-sm font-normal text-foreground [font-family:inherit] shadow-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0 dark:border-slate-600 dark:bg-slate-800";
const configLabelClass = "text-sm font-medium text-muted-foreground";

export default function TenantCloudConfigureDialog({
    open,
    onOpenChange,
    cloudProvider,
    config,
    cloudConfigData,
    setCloudConfigData,
    onConfigured,
    subUserReg
}: TenantCloudConfigureDialogProps) {

    const configCloud = cloudConfig[cloudProvider];
    const CloudIcon = configCloud.icon;
    const [submitState, setSubmitState] = useState<"idle" | "loading" | "success" | "error">("idle");
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (open) {
            setSubmitState("idle");
            setCloudConfigData({
                azureDisplayName: "",
                azureId: "",
                displayName: "",
                gcpUserName: "",
                gcpAccountEmail: "",
                awsCredentials: [],
            });
        }
        return () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, [open, cloudProvider, setCloudConfigData]);

    const handleSubmit = async () => {
        setSubmitState("loading");
        try {
            const configured = await subUserReg();
            if (!configured) {
                setSubmitState("error");
                return;
            }
            setSubmitState("success");
            closeTimerRef.current = setTimeout(() => {
                onConfigured();
                onOpenChange(false);
            }, 1250);
        } catch {
            setSubmitState("error");
        }
    };


    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (submitState === "loading" || submitState === "success") return;
                onOpenChange(nextOpen);
            }}
        >
            <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto !rounded-2xl sm:!rounded-2xl border border-slate-200 bg-card text-foreground [font-family:inherit] shadow-xl dark:border-slate-700">
                <DialogHeader className="pb-2">
                    <DialogTitle className="flex items-center gap-3">
                        <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <CloudIcon className="w-6 h-6 text-primary" />
                        </span>
                        <span>
                            <span className="block text-xl font-semibold text-foreground">Configure {config.name} Account</span>
                            <span className="block text-sm font-normal text-muted-foreground mt-1">
                                Add your cloud identity to request resource access.
                            </span>
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {cloudProvider === "azure" && (
                        <>
                            <div className="space-y-1.5">
                                <Label className={configLabelClass}>Display Name *</Label>
                                <Input
                                    className={configInputClass}
                                    placeholder="Enter Display Name"
                                    value={cloudConfigData.azureDisplayName}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, azureDisplayName: e.target.value, displayName: e.target.value })
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className={configLabelClass}>Azure ID *</Label>
                                <Input
                                    className={configInputClass}
                                    placeholder="Enter Azure ID"
                                    value={cloudConfigData.azureId}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, azureId: e.target.value })
                                    }
                                />
                            </div>
                        </>
                    )}

                    {cloudProvider === "gcp" && (
                        <>
                            <div className="space-y-1.5">
                                <Label className={configLabelClass}>GCP User Name *</Label>
                                <Input
                                    className={configInputClass}
                                    placeholder="Enter GCP User Name"
                                    value={cloudConfigData.gcpUserName}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, gcpUserName: e.target.value })
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className={configLabelClass}>GCP Account Email *</Label>
                                <Input
                                    className={configInputClass}
                                    placeholder="Enter GCP Account Email"
                                    value={cloudConfigData.gcpAccountEmail}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, gcpAccountEmail: e.target.value })
                                    }
                                />
                            </div>
                        </>
                    )}

                    {cloudProvider === "aws" && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className={configLabelClass}>AWS Credentials ({cloudConfigData.awsCredentials.length})</Label>
                                <Button
                                    size="sm"
                                    className="gap-2 rounded-xl"
                                    onClick={() =>
                                        setCloudConfigData({
                                            ...cloudConfigData,
                                            awsCredentials: [
                                                ...cloudConfigData.awsCredentials,
                                                { id: Date.now(), awsUserName: "", awsAccountARN: "" },
                                            ],
                                        })
                                    }
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Credential
                                </Button>
                            </div>

                            {cloudConfigData.awsCredentials.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-6 rounded-2xl border border-dashed border-border">
                                    No credentials added yet.
                                </p>
                            ) : (
                                cloudConfigData.awsCredentials.map((cred: any) => (
                                    <div key={cred.id} className="border border-border rounded-2xl p-4 space-y-3 bg-card portal-card-hover">
                                        <div className="space-y-1.5">
                                            <Label className={configLabelClass}>Username</Label>
                                            <Input
                                                className={configInputClass}
                                                placeholder="Enter AWS username"
                                                value={cred.awsUserName || cred.username || ""}
                                                onChange={(e) =>
                                                    setCloudConfigData({
                                                        ...cloudConfigData,
                                                        awsCredentials: cloudConfigData.awsCredentials.map((c: any) =>
                                                            c.id === cred.id ? { ...c, awsUserName: e.target.value } : c
                                                        ),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className={configLabelClass}>ARN</Label>
                                            <Input
                                                className={configInputClass}
                                                placeholder="Enter AWS account ARN"
                                                value={cred.awsAccountARN || cred.arn || ""}
                                                onChange={(e) =>
                                                    setCloudConfigData({
                                                        ...cloudConfigData,
                                                        awsCredentials: cloudConfigData.awsCredentials.map((c: any) =>
                                                            c.id === cred.id ? { ...c, awsAccountARN: e.target.value } : c
                                                        ),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="flex justify-end">
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className="gap-2 rounded-xl"
                                                onClick={() =>
                                                    setCloudConfigData({
                                                        ...cloudConfigData,
                                                        awsCredentials: cloudConfigData.awsCredentials.filter(
                                                            (c: any) => c.id !== cred.id
                                                        ),
                                                    })
                                                }
                                            >
                                                <Trash2 className="w-4 h-4" />
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {submitState === "success" && (
                        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                            User configured successfully.
                        </div>
                    )}
                    {submitState === "error" && (
                        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            User configuration failed.
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-3">
                    <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => onOpenChange(false)}
                        disabled={submitState === "loading" || submitState === "success"}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="rounded-xl"
                        onClick={handleSubmit}
                        disabled={
                            submitState === "loading" ||
                            submitState === "success" ||
                            (cloudProvider === "azure" &&
                                (!cloudConfigData.azureDisplayName || !cloudConfigData.azureId)) ||
                            (cloudProvider === "gcp" &&
                                (!cloudConfigData.gcpUserName || !cloudConfigData.gcpAccountEmail)) ||
                            (cloudProvider === "aws" &&
                                (cloudConfigData.awsCredentials.length === 0 ||
                                    cloudConfigData.awsCredentials.some(
                                        (credential: any) =>
                                            !(credential.awsUserName || credential.username)?.trim() ||
                                            !(credential.awsAccountARN || credential.arn)?.trim()
                                    )))
                        }
                    >
                        {submitState === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {submitState === "loading" ? "Configuring..." : "Save Configuration"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
